/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'
import {
  bucketEventsByDay,
  calendarEntryKey,
  interleaveByStart,
} from '@sportninja/common/features/event/calendarItems'
import type { CalendarEntry } from '@sportninja/common/features/event/calendarItems'

import { font, weightRegular, weightSemiBold } from 'src/components/css'
import type { CalendarAgendaViewProps } from './types'
import type { Game } from '../game-list-row/types'
import CalendarGameCard from './CalendarGameCard'
import CalendarEventCard from './CalendarEventCard'
import { formatCivilDay, todayKey } from './buildCalendarDays'
import { calendarLabel } from './calendarI18n'

const CalendarAgendaView = ({
  items,
  events,
  isSoccer,
  isHockey,
  loading,
}: CalendarAgendaViewProps) => {
  'use memo'

  if (loading) {
    return (
      <div css={loadingStyles}>
        <Spin size='small' />
      </div>
    )
  }

  //  Venue-local grouping via the shared bucketer, so the agenda cannot drift
  //  from the month grid, the day grid or the list on the same dataset.
  const groups = groupGamesByDay(items as unknown as GameLike[])
  const eventsByDay = bucketEventsByDay(events)
  const today = todayKey()

  //  A day carrying ONLY events still needs a heading — `groupGamesByDay` has
  //  no bucket for it, so those days are added here and the whole set resorted
  //  rather than appended out of order at the end.
  const dayKeys = Array.from(
    new Set([
      ...groups.map((group) => group.dayKey),
      ...Array.from(eventsByDay.keys()),
    ])
  ).sort()

  if (dayKeys.length === 0) {
    return <div css={emptyStyles}>{calendarLabel('calendarNothingPeriod')}</div>
  }

  const groupsByDay = new Map(groups.map((group) => [group.dayKey, group]))

  return (
    <div css={containerStyles}>
      {dayKeys.map((dayKey) => {
        const group = groupsByDay.get(dayKey)
        const isToday = dayKey === today
        //  TBD games keep their band ahead of everything timed; timed games and
        //  events then interleave by start time, matching the month/week cells.
        const ordered: CalendarEntry[] = [
          ...(group?.tbdGames ?? []).map((game) => ({
            kind: 'game' as const,
            game,
          })),
          ...interleaveByStart(
            group?.games ?? [],
            eventsByDay.get(dayKey) ?? []
          ),
        ]

        return (
          <div key={dayKey} css={dayGroupStyles}>
            <div css={dayHeaderStyles(isToday)}>
              <div>
                <div css={dateTextStyles(isToday)}>
                  {formatCivilDay(dayKey, 'cccc, LLLL d, yyyy')}
                </div>
                <div css={gameCountStyles}>
                  {/* "N scheduled" rather than "N games scheduled" — the count
                      now covers events too, and calling a practice a game
                      would be wrong on a day that has one of each. */}
                  {calendarLabel('calendarItemsScheduled', {
                    count: ordered.length,
                  })}
                </div>
              </div>
              {isToday && (
                <span css={todayBadgeStyles}>
                  {calendarLabel('calendarToday')}
                </span>
              )}
            </div>
            <div css={gamesContainerStyles}>
              {ordered.map((entry) =>
                entry.kind === 'event' ? (
                  <CalendarEventCard
                    key={calendarEntryKey(entry)}
                    event={entry.event}
                  />
                ) : (
                  <CalendarGameCard
                    key={calendarEntryKey(entry)}
                    game={entry.game as Game}
                    isSoccer={isSoccer}
                    isHockey={isHockey}
                  />
                )
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const dayGroupStyles = css`
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const dayHeaderStyles = (isToday: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${colors.SECONDARY_400};
  ${isToday ? `box-shadow: inset 3px 0 0 ${colors.PRIMARY};` : ''}
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const dateTextStyles = (isToday: boolean) => css`
  ${font.body}
  ${weightSemiBold}
  font-size: 14px;
  color: ${isToday ? colors.PRIMARY : colors.NEUTRAL_0};
  line-height: 21px;
`

const gameCountStyles = css`
  ${font.body}
  ${weightRegular}
  font-size: 13px;
  color: ${colors.NEUTRAL_70};
  line-height: 16px;
`

const todayBadgeStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${colors.PRIMARY_TEXT};
  background: ${colors.PRIMARY};
  padding: 4px 12px;
  border-radius: 4px;
  flex-shrink: 0;
`

const gamesContainerStyles = css`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${colors.SECONDARY_500};
`

const loadingStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
`

const emptyStyles = css`
  ${font.body}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  background: ${colors.SECONDARY_500};
`

export default CalendarAgendaView
